import {
  Button,
  FormError,
  Input,
  Label,
  QueryView,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@simplicity/ui";
import { useState } from "react";
import {
  parseSourceRanges,
  placementErrors,
  placementValues,
  workspaceNameError,
  type PlacementForm,
} from "./onsiteModel";
import { PlacementFields } from "./PlacementFields";
import { ServerFields } from "./ServerFields";
import type { CreateWorkspaceInput, OnsiteWorkspace } from "./types";
import { useCreateWorkspace, useRunnerProjects, useWarehouseProject } from "./useOnsite";

interface FormState extends PlacementForm {
  name: string;
  projectId: string;
  /** What the operator typed; null means "use the runner Project's BigQuery destination". */
  gcpProjectId: string | null;
  enableSftpServer: boolean;
  sshSourceRanges: string;
}

const INITIAL: FormState = {
  name: "",
  projectId: "",
  gcpProjectId: null,
  enableSftpServer: true,
  sshSourceRanges: "0.0.0.0/0",
  region: "",
  zone: "",
};

const PLACEMENT_KEYS = ["region", "zone"] as const;

type SetField = <K extends keyof FormState>(key: K, value: FormState[K]) => void;

function NameField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const error = value ? workspaceNameError(value) : null;
  return (
    <div className="mb-4">
      <Label htmlFor="onsite-name" className="form-label">
        Workspace name
      </Label>
      <Input
        id="onsite-name"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="e.g. ds"
        aria-invalid={error ? true : undefined}
        aria-describedby="onsite-name-hint"
        className="max-w-48"
        autoComplete="off"
      />
      <p id="onsite-name-hint" className="mt-1 text-xs text-muted-foreground">
        At most 8 characters. The SFTP function&apos;s service account is named{" "}
        <code>&lt;name&gt;-simplicity-sftp-fn-sa</code>, and GCP caps a service-account id at 30
        characters.
      </p>
      {error && <FormError>{error}</FormError>}
    </div>
  );
}

function ProjectField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const projectsQuery = useRunnerProjects();
  return (
    <div className="mb-4">
      <Label htmlFor="onsite-project" className="form-label">
        Runner project
      </Label>
      <QueryView
        query={projectsQuery}
        loading={<p className="text-xs text-muted-foreground">Loading projects…</p>}
        error={(err) => <FormError>Failed to load projects: {err.message}</FormError>}
      >
        {(projects) => (
          <Select value={value} onValueChange={onChange}>
            <SelectTrigger id="onsite-project" className="w-full max-w-sm">
              <SelectValue placeholder="Select a project" />
            </SelectTrigger>
            <SelectContent>
              {projects.map((project) => (
                <SelectItem key={project.id} value={project.id}>
                  {project.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </QueryView>
    </div>
  );
}

const hintClass = "mt-1 text-xs text-muted-foreground";

function WarehouseHint({ projectId, override }: { projectId: string; override: string | null }) {
  const warehouse = useWarehouseProject(projectId);
  return (
    <QueryView
      query={warehouse}
      loading={<p className={hintClass}>Looking up this project&apos;s BigQuery destination…</p>}
      error={(err) => (
        <FormError>Couldn&apos;t read this project&apos;s destinations ({err.message}).</FormError>
      )}
      empty={
        <p className={hintClass}>
          This project has no single BigQuery destination. Enter the client&apos;s GCP project id.
        </p>
      }
    >
      {(derived) => (
        <p className={hintClass}>
          {override === null || override === derived
            ? "From this project's BigQuery destination."
            : `Differs from this project's BigQuery destination (${derived}).`}
        </p>
      )}
    </QueryView>
  );
}

function GcpProjectField({
  projectId,
  value,
  override,
  onChange,
}: {
  projectId: string;
  value: string;
  override: string | null;
  onChange: (v: string) => void;
}) {
  return (
    <div className="mb-4">
      <Label htmlFor="onsite-gcp-project" className="form-label">
        GCP project id
      </Label>
      <Input
        id="onsite-gcp-project"
        value={value}
        onChange={(e) => onChange(e.target.value.trim())}
        placeholder="e.g. client-data-123456"
        className="max-w-sm"
        autoComplete="off"
      />
      {projectId !== "" && <WarehouseHint projectId={projectId} override={override} />}
    </div>
  );
}

function canSubmit(form: FormState, gcpProjectId: string): boolean {
  return (
    workspaceNameError(form.name) === null &&
    form.projectId !== "" &&
    gcpProjectId !== "" &&
    Object.keys(placementErrors(form)).length === 0
  );
}

function createInput(form: FormState, gcpProjectId: string): CreateWorkspaceInput {
  return {
    ...form,
    gcpProjectId,
    sshSourceRanges: parseSourceRanges(form.sshSourceRanges),
    ...placementValues(form),
  };
}

export function CreateWorkspaceForm({
  onCreated,
  onCancel,
}: {
  onCreated: (workspace: OnsiteWorkspace) => void;
  onCancel: () => void;
}) {
  const [form, setForm] = useState<FormState>(INITIAL);
  const create = useCreateWorkspace();
  const derived = useWarehouseProject(form.projectId).data ?? null;
  const gcpProjectId = form.gcpProjectId ?? derived ?? "";
  const setField: SetField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  // A new runner Project brings its own BigQuery destination, so drop any typed-in override.
  const selectProject = (projectId: string) =>
    setForm((prev) => ({ ...prev, projectId, gcpProjectId: null }));

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit(form, gcpProjectId)) return;
    create.mutate(createInput(form, gcpProjectId), { onSuccess: onCreated });
  }

  return (
    <form onSubmit={handleSubmit} className="form-card mb-6 max-w-[640px]">
      <h3 className="mb-4 text-sm font-semibold text-foreground">Register a workspace</h3>
      <NameField value={form.name} onChange={(v) => setField("name", v)} />
      <ProjectField value={form.projectId} onChange={selectProject} />
      <GcpProjectField
        projectId={form.projectId}
        value={gcpProjectId}
        override={form.gcpProjectId}
        onChange={(v) => setField("gcpProjectId", v)}
      />
      <ServerFields
        idPrefix="onsite"
        enableSftpServer={form.enableSftpServer}
        sshSourceRanges={form.sshSourceRanges}
        onSftpChange={(v) => setField("enableSftpServer", v)}
        onRangesChange={(v) => setField("sshSourceRanges", v)}
      />
      <PlacementFields
        idPrefix="onsite"
        keys={PLACEMENT_KEYS}
        values={form}
        errors={placementErrors(form)}
        onChange={setField}
      />
      {create.error && (
        <FormError assertive className="whitespace-pre-line">
          {create.error.message}
        </FormError>
      )}
      <div className="form-actions">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={create.isPending || !canSubmit(form, gcpProjectId)}>
          {create.isPending ? "Registering..." : "Register"}
        </Button>
      </div>
    </form>
  );
}
